import React, { useState, useEffect } from "react";
import { CssVarsProvider, extendTheme } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import {
  Box,
  Typography,
  Textarea,
  Input,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  Option,
} from "@mui/joy";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useQuery, gql, useMutation } from "@apollo/client";
import { GlobalStyles } from "@mui/joy";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";

const PROFILE_QUERY = gql`
  query Profile {
    profile {
      id
      firstName
      lastName
      email
      phoneNumber
      role
    }
  }
`;

const CREATE_ANONYMOUS_SUPPORT_TICKET = gql`
  mutation CreateAnonymousSupportTicket(
    $input: CreateAnonymousSupportTicketInput!
  ) {
    createAnonymousSupportTicket(input: $input) {
      supportTicket {
        id
        title
      }
    }
  }
`;

const CREATE_SUPPORT_TICKET = gql`
  mutation CreateSupportTicket($input: CreateSupportTicketInput!) {
    createSupportTicket(input: $input) {
      supportTicket {
        id
        title
        submitter {
          id
          firstName
        }
      }
    }
  }
`;

const Support = ({ token }: { token?: string }) => {
  const { data, loading, error } = useQuery(PROFILE_QUERY, {
    skip: !token,
  });
  const [createSupportTicket] = useMutation(CREATE_SUPPORT_TICKET);
  const [createAnonymousSupportTicket] = useMutation(
    CREATE_ANONYMOUS_SUPPORT_TICKET
  );

  const profile = data?.profile;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    color: "success" | "danger";
  }>({ open: false, message: "", color: "success" });

  const handleSubmit = async () => {
    try {
      if (profile) {
        const res = await createSupportTicket({
          variables: {
            input: {
              title,
              description,
            },
          },
        });

        if (res.data.createSupportTicket.errors?.length) {
          setSnackbar({
            open: true,
            message: "Error: " + res.data.createSupportTicket.errors.join(", "),
            color: "danger",
          });
          return;
        }
      } else {
        const res = await createAnonymousSupportTicket({
          variables: {
            input: {
              title,
              description,
              email,
              name,
              phoneNumber,
              role,
            },
          },
        });

        if (res.data.createAnonymousSupportTicket.errors?.length) {
          setSnackbar({
            open: true,
            message:
              "Error: " +
              res.data.createAnonymousSupportTicket.errors.join(", "),
            color: "danger",
          });
          return;
        }
      }

      setSnackbar({
        open: true,
        message: "Support request submitted!",
        color: "success",
      });

      setTitle("");
      setDescription("");
      if (!profile) {
        setName("");
        setEmail("");
        setPhoneNumber("");
        setRole("");
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "Error: " + err.message,
        color: "danger",
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  const customTheme = extendTheme({ defaultColorScheme: "dark" });

  if (error) {
    return <Typography color="danger">Error: {error.message}</Typography>;
  }

  return (
    <CssVarsProvider theme={customTheme} disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ":root": {
            "--Form-maxWidth": "800px",
            "--Transition-duration": "0.4s",
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          px: 2,
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 400,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            my: "auto",
            py: 2,
            pb: 5,
            gap: 2,
            borderRadius: "sm",
            "& form": {
              display: "flex",
              flexDirection: "column",
              gap: 2,
            },
            [`& .MuiFormLabel-asterisk`]: {
              visibility: "hidden",
            },
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={
                  profile ? `${profile?.firstName} ${profile?.lastName}` : name
                }
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={!!profile}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={profile ? profile?.email : email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={!!profile}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Phone number</FormLabel>
              <Input
                value={profile ? profile?.phoneNumber : phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Your phone number"
                disabled={!!profile}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Role</FormLabel>
              <Select
                name="role"
                required
                value={profile ? profile?.role : role}
                onChange={(_, newValue) => setRole(newValue as string)}
                disabled={!!profile}
              >
                <Option value="ASSET_OWNER">Asset Owner</Option>
                <Option value="BACK_OFFICE_OFFICIAL">
                  Back Office Official
                </Option>
                <Option value="FIELD_OFFICIAL">Field Official</Option>
                <Option value="INTEGRATOR">Integrator</Option>
                <Option value="PARTNER">Partner</Option>
                <Option value="SERVICE_PROVIDER">Service Provider</Option>
                <Option value="SYSTEM_ADMINISTRATOR">
                  System Administrator
                </Option>
                <Option value="SYSTEM_SERVICE_SUPPORT">
                  System Service Support
                </Option>
                <Option value="SYSTEM_SUPPORT">System Support</Option>
                <Option value="KIOSK">Kiosk</Option>
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Title</FormLabel>
              <Textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary of your issue"
                minRows={1}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your issue in detail..."
                minRows={6}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              disabled={!title || !description || (!profile && !email)}
              sx={{
                backgroundColor: "#003366",
                color: "#FFC107",
                "&:hover": { backgroundColor: "#002244" },
                "&:disabled": { backgroundColor: "#ccc", color: "#666" },
              }}
            >
              Submit
            </Button>
          </form>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          color={snackbar.color}
          variant="soft"
          startDecorator={
            snackbar.color === "success" ? <CheckCircle /> : <ErrorIcon />
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CssVarsProvider>
  );
};

export default Support;
